// components/ImportTable.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table } from "react-bootstrap";
import AMSSelect from "../../../components/AMSSelect";
import RoundedGreenTick from "../../../assets/icons/RoundedGreenTick";
import WarningIcon from "../../../assets/icons/WarningIcon";
import styled from "styled-components";
import StyledText from "../../../components/StyledComponents/StyledText";
import { font, Theme } from "../../../utils/themes";
import StyledCheckBox from "../../../components/StyledComponents/CheckBox/StyledCheckBox";
import { useTheme } from "../../../middleware/ThemeContext";
import TableSkeleton from "../../../components/Skeleton/Table";
import { useGetAllFieldsAllPagesQuery } from "../../../api/formsApiSlice";

interface RowData {
    import: boolean;
    fileColumn: string;
    preview: string;
    mappedField: { label: string; value: string } | null;
}
interface ImportTableProps {
    columns: string[];
    previewRows: string[][];
    suggestedMapping: Record<string, string | null>;
    // fieldOptions: { value: string; label: string }[]; // dropdown options from API
    onChange?: (rows: RowData[]) => void; // 🔹 Final payload callback
    addNewField?: (inputValue?: string) => void;
    title?: string;
    subHeading?: string;
    isLoading?: boolean;
    isFetching?: boolean;
    scopeId?: string
}

type FieldSelectOption = {
    label: string;
    value: string; // accessKey
    accessKey: string;
    id: string; // original field id (incoming as `fieldOptions.value`)
    dataType?: string;
};

const ImportTable: React.FC<ImportTableProps> = ({
    columns,
    previewRows,
    suggestedMapping,
    onChange,
    addNewField,
    title,
    subHeading,
    isLoading,
    isFetching,
    scopeId,
}) => {
    const [rows, setRows] = useState<RowData[]>([]);
    const { theme } = useTheme();

    const { data: fieldsData = [], isLoading: isLoadingFields, isFetching: isFetchingFields, } = useGetAllFieldsAllPagesQuery({
        scopeId,
        importData: true,
        perPage: 50,
    }, {
        skip: false,
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (columns.length > 0 && rows.length === 0) {

            const initialRows = columns.map((col, idx) => {
                const mapped = suggestedMapping[col] || "";
                return {
                    import: Boolean(mapped), // ✅ agar mapping hai to true warna false
                    fileColumn: col,
                    preview: previewRows[0]?.[idx] || "",
                    mappedField: mapped ? { label: mapped, value: mapped } : null
                };
            });
            setRows(initialRows);
        }
    }, [columns, previewRows, suggestedMapping, rows.length]);


    // Emit changes upwards
    useEffect(() => {
        if (onChange) onChange(rows);
    }, [rows]);

    const handleCheckboxChange = (index: number, checked: boolean) => {

        setRows((prev) => {
            const updated = [...prev];
            const mappedValue = updated[index].mappedField?.value;

            if (!mappedValue?.trim()) {
                updated[index].import = false;
            } else {
                updated[index].import = checked;
            }

            return updated;
        });
    };

    const handleFieldChange = (index: number, value: { label: string; value: string } | null) => {
        setRows(prev => {
            const updated = [...prev];

            if (value) {
                // `value.value` is the field accessKey (see FieldSelectOption)
                updated[index].mappedField = { label: value.label, value: value.value };

                // ✅ Auto-check the import checkbox when a field is selected
                updated[index].import = true;
            } else {
                updated[index].mappedField = null;
                // ✅ Auto-uncheck when field is cleared
                updated[index].import = false;
            }

            return updated;
        });
    };

    const fieldSelectOptions: FieldSelectOption[] = useMemo(() => {
        const items = Array.isArray(fieldsData) ? fieldsData : [];
        const mapped = items
            .filter((f: any) => Boolean(f?.accessKey))
            .map((field: any) => ({
                label: field?.name ?? field?.label ?? String(field?.accessKey ?? ""),
                value: String(field?.accessKey ?? ""),
                accessKey: String(field?.accessKey ?? ""),
                id: String(field?.id ?? field?.value ?? field?.accessKey ?? ""),
                dataType: field?.dataType,
            }));

        // Backend can return multiple rows representing the same logical field (same accessKey)
        // e.g. metadata_* + numeric id variants. Since mapping is by accessKey, dedupe by accessKey.
        const seen = new Set<string>();
        return mapped.filter((opt) => {
            const key = String(opt?.accessKey ?? opt?.value ?? "");
            if (!key) return false;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [fieldsData]);

    const handleAddNewField = useCallback((inputValue?: string) => {
        if (addNewField) {
            addNewField(inputValue); // ✅ pass the inputValue!
        }
    }, [addNewField]);

    const headers = [
        { name: "Import", type: "text" as const },
        { name: "File Column", type: "text" as const },
        { name: "Mapped", type: "text" as const },
        { name: "Fields", type: "text" as const },
    ]


    return (
        <>
            <div className="px-4">

                <div className="d-flex justify-content-between align-items-center">
                    <div className="mb-2 d-flex flex-column gap-1">
                        <StyledText fontWeight={700} fontFamily={font.raleway} fontSize="20px">
                            {title}
                        </StyledText>
                        <StyledText fontWeight={400} fontFamily={font.raleway} fontSize="14px" textColor={theme.CorporateTheme.colors.textSecondary}>
                            {subHeading}
                        </StyledText>
                    </div>
                </div>
                <hr className="mb-4 border-secondary" />
                {isLoading ? <TableSkeleton rowCount={10} columns={headers} /> :
                    <StyledHeight>
                        <Table responsive >
                            <StyledHeader theme={theme}>
                                <tr>
                                    {headers.map((header, idx) => (
                                        <th key={idx} className={`${idx !== 1 && 'text-center'} ${idx === 0 ? 'w-10' : 'w-20'}`}>
                                            {header?.name}
                                        </th>
                                    ))}
                                </tr>
                            </StyledHeader>
                            <StyledTableBody theme={theme}>

                                {rows.map((row, idx) => {
                                    const isMapped = Boolean(row?.mappedField?.value?.trim());

                                    return (
                                        <tr key={idx}>
                                            {/* Import Checkbox */}
                                            <td className="text-center align-middle">
                                                <StyledCheckBox
                                                    className="cursor-pointer d-flex align-items-center justify-content-center rounded-1"
                                                    size={16}
                                                    disabled={!row.mappedField?.value?.trim()}
                                                    borderColor={
                                                        row.import
                                                            ? theme.CorporateTheme?.colors?.primary
                                                            : "#626262"
                                                    }
                                                    bgColor={theme.CorporateTheme?.colors?.primary}
                                                    checkColor={theme.colors.contentBackground}
                                                    value={row.import}
                                                    onChange={() => handleCheckboxChange(idx, !row.import)}
                                                />
                                            </td>

                                            {/* File Columns with preview */}
                                            <td className="align-middle">
                                                <div>
                                                    <StyledText fontSize="13px" fontFamily="var(--font-raleway)" fontWeight={600} textColor={theme.CorporateTheme.colors.textSecondary}>{row.fileColumn}</StyledText>
                                                </div>
                                                <StyledText fontSize="12px" fontFamily="var(--font-raleway)" fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary} className="mt-1">{row.preview}</StyledText>
                                            </td>

                                            {/* Mapped */}
                                            <td className="text-center align-middle">
                                                {isMapped ? (
                                                    <RoundedGreenTick />
                                                ) : (
                                                    <WarningIcon />
                                                )}
                                            </td>

                                            {/* Fields Dropdown */}
                                            <td className="align-middle px-4">
                                                <AMSSelect<FieldSelectOption>
                                                    showDataType
                                                    includeAccessKey
                                                    placeholder="Search field..."
                                                    staticOptions={fieldSelectOptions}
                                                    // AsyncPaginate caches results; invalidate cache when options update.
                                                    cacheUniqs={[fieldSelectOptions.length,]}
                                                    isLoading={isFetchingFields || isLoadingFields}
                                                    isClearable={false}
                                                    getOptionValue={(opt) => String(opt?.accessKey ?? opt?.value ?? opt?.id ?? "")}
                                                    value={
                                                        row.mappedField?.value
                                                            ? (fieldSelectOptions.find((o) => o.value === row.mappedField?.value) ?? null)
                                                            : null
                                                    }
                                                    onChange={(option) => {
                                                        handleFieldChange(
                                                            idx,
                                                            option ? { label: option.label, value: option.value } : null
                                                        );
                                                    }}
                                                    includeAddButton
                                                    addButtonText="Add Field"
                                                    onAddButtonClick={(term) => handleAddNewField(term)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </StyledTableBody>
                        </Table>
                    </StyledHeight>}
            </div>
        </>
    );
};


const StyledHeader = styled.thead<{ theme: Theme }> `
 
  th {
      background: ${({ theme }) => theme?.CorporateTheme?.colors?.filterBG}; !important;
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.headerColor} !important;
    border: none !important;
    padding: 10px; /* optional: thoda spacing */
    // text-align: center; /* optional: alignment */
    font-weight: 600;
    font-size: 13px;
    font-family: var(--font-raleway);
  }
`;

const StyledTableBody = styled.tbody<{ theme: Theme }> `
    tr {
        /* Alternating row backgrounds */
        &:nth-child(even) {
        background-color: ${({ theme }) => theme.CorporateTheme?.colors?.headerBG ?? '#f8f9fa'};
        }
        &:nth-child(odd) {
        background-color: ${({ theme }) => theme.CorporateTheme?.colors?.background ?? '#ffffff'};
        }

        /* Hover effect */
        &:hover {
        background-color: ${({ theme }) => theme.CorporateTheme?.colors?.headerBG ?? '#e9ecef'};
        }

        /* Ensure td doesn’t override row background */
        td {
        background: transparent !important;
        color: ${({ theme }) => theme.CorporateTheme?.colors?.headerColor ?? '#000000'} !important;
        }
    }
`;

const StyledHeight = styled.div`
  max-height: calc(100vh - 302px);
  overflow-y: auto;
`;


const StyledField = styled.div<{ theme: Theme }>`
display: flex;
align-items: center;
background: ${({ theme }) => theme?.CorporateTheme?.colors?.filterBG} !important;
padding: 10px; /* optional: thoda spacing */
border-radius: 5px;
height: 40px;
pointer-events: none;
opacity: 0.7;
`
export default ImportTable;